/** CRM i18n labels (F3-I). Map the backend's stable keys to text. */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { Source, Stage } from "./api";

export const STAGE_LABEL: Record<Stage, MessageDescriptor> = {
  new: msg`New`,
  contacted: msg`Contacted`,
  visit: msg`Visit`,
  enrolled: msg`Enrolled`,
  lost: msg`Lost`,
};

export const SOURCE_LABEL: Record<Source, MessageDescriptor> = {
  landing: msg`Landing`,
  walk_in: msg`Walk-in`,
  referral: msg`Referral`,
  instagram: msg`Instagram`,
  whatsapp: msg`WhatsApp`,
  other: msg`Other`,
  // Automatic origins (`crm::funnel`). They were missing here while the
  // database had accepted them since 00090, so every self-registered lead
  // rendered `t(undefined)` in the kanban and in the funnel breakdown.
  signup: msg`Open sign-up`,
  free_enrollment: msg`Free enrollment`,
};

const FIELD_LABEL: Record<string, MessageDescriptor> = {
  stage: msg`Stage`,
  source: msg`Source`,
  assigned_to: msg`Assigned to`,
  interest_course_id: msg`Course of interest`,
  interest: msg`Interest (text)`,
  created_days_ago: msg`Created (days ago)`,
  debt_usd_minor: msg`Debt (USD minor)`,
  last_active_days: msg`Inactive (days)`,
  cohort_status: msg`Cohort status`,
  national_id: msg`National ID`,
  campus_id: msg`Campus`,
};

const OP_LABEL: Record<string, MessageDescriptor> = {
  eq: msg`is`,
  neq: msg`is not`,
  gt: msg`greater than`,
  lt: msg`less than`,
  contains: msg`contains`,
  in: msg`in`,
};

const EVENT_LABEL: Record<string, MessageDescriptor> = {
  note: msg`Note`,
  call: msg`Call`,
  whatsapp: msg`WhatsApp`,
  email: msg`Email`,
  visit: msg`Visit`,
  stage_change: msg`Stage change`,
  created: msg`Created`,
};

// Safe accessors: the maps are indexed by `string`, so they return a
// defined descriptor with a fallback (avoids `MessageDescriptor | undefined`).
const FALLBACK: MessageDescriptor = msg`—`;
export const fieldLabel = (f: string): MessageDescriptor => FIELD_LABEL[f] ?? FALLBACK;
/**
 * Safe accessors for the two catalogs the SERVER can widen without the
 * client noticing. `source` and `stage` arrive as raw strings, so indexing
 * the map directly returns `undefined` and `t(undefined)` renders nothing —
 * which is how the automatic origins stayed invisible.
 */
// Widened to `string` keys by assignment, not by a type assertion: the maps
// have to stay `Record<Source|Stage, …>` so a missing entry fails
// `check-types`, but the lookup receives whatever the server sent.
const SOURCE_BY_KEY: Record<string, MessageDescriptor> = SOURCE_LABEL;
const STAGE_BY_KEY: Record<string, MessageDescriptor> = STAGE_LABEL;
export const sourceLabel = (s: string): MessageDescriptor => SOURCE_BY_KEY[s] ?? FALLBACK;
export const stageLabel = (s: string): MessageDescriptor => STAGE_BY_KEY[s] ?? FALLBACK;
export const opLabel = (o: string): MessageDescriptor => OP_LABEL[o] ?? FALLBACK;
export const eventLabel = (k: string): MessageDescriptor => EVENT_LABEL[k] ?? FALLBACK;
